import { catchError, first, map, switchMap } from 'rxjs/operators';
import { select, Store } from '@ngrx/store';
import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { BehaviorSubject, Observable, of } from 'rxjs';
import moment from 'moment';
import { TranslateService } from '@ngx-translate/core';
import { environment } from '../../../environments/environment';
import { Survey } from '../_models/survey';
import { User } from '../_models/user';
import { Equipment } from '../_models';
import { AppState } from '../_reducers';
import * as UserActions from '../../common/_actions/user';
import { selectAllUsers } from '../_selectors/user.selectors';
import { selectCurrentUser } from '../_selectors/currentUser.selectors';

@Injectable()
export class UserService {
  private apiBaseUrl = environment.apiBaseUrl;
  userList$: Observable<User[]>;
  private userMe$ = new BehaviorSubject<User>(null);

  commoditySetting: any[];
  isSupplyChainDestinationFilterActive: boolean;
  showSupplyChainDestinationFilter: boolean;

  currentUser: User = null;

  constructor(
    private http: HttpClient,
    private store: Store<AppState>,
    private translate: TranslateService
  ) {
    this.userList$ = this.store.pipe(select(selectAllUsers));
    this.store.select(selectCurrentUser).subscribe((user) => {
      this.userMe$.next(user);
      this.currentUser = user;
    });
  }

  getFromLocalStore(): Observable<User[]> {
    return this.userList$;
  }

  getUserMeFromLocalStore(): Observable<User> {
    return this.userMe$.asObservable();
  }
  getAllMultiFarmUsers(): Observable<User[]> {
    return this.http
      .get<any>(this.apiBaseUrl + 'api/private/v2/auth/users?all=true')
      .pipe(map((response) => response));
  }
  getUsers(): Observable<User[]> {
    return this.http.get<any>(this.apiBaseUrl + 'api/private/v2/auth/users').pipe(
      map((response) => {
        return this.manageRemovedUser(response);
      })
    );
  }
  getUser(): Observable<User> {
    return this.http
      .get<any>(this.apiBaseUrl + 'api/private/v2/auth/users/me')
      .pipe(map((response) => response));
  }
  saveUser(user): Observable<User> {
    const userCopy = { ...user };

    delete userCopy.lastUpdated;
    delete userCopy.dateCreated;
    let userMultiFarmToken = null;
    if (userCopy && userCopy.userMultiFarmToken) {
      userMultiFarmToken = userCopy.userMultiFarmToken;
    }
    delete userCopy.userMultiFarmToken;

    if (userCopy.farm) {
      const idfarm: any = {
        id: userCopy.farm.id,
      };
      userCopy.farm = idfarm;
    }

    let userStore = null;
    this.getUserMeFromLocalStore().subscribe((u: any) => {
      userStore = u;
    });

    if (userStore && userStore.id) {
      // TODO Per Multifarm???
      if (userCopy.id) {
        return this.http
          .put(this.apiBaseUrl + 'api/private/v2/auth/users/' + userCopy.id, userCopy)
          .pipe(
            map((response) => {
              if (response) {
                const responseUser: any = response;
                if (
                  user &&
                  this.currentUser &&
                  this.currentUser.id &&
                  user.id &&
                  !user.userMultiFarmToken &&
                  user.id === this.currentUser.id
                ) {
                  responseUser.userMultiFarmToken = userMultiFarmToken;
                  this.store.dispatch(UserActions.updateUserSuccess({ user: responseUser }));
                }
                return responseUser;
              }
            })
          );
      } else {
        if (userStore.id !== userCopy.id) {
          const companySelected = JSON.parse(localStorage.getItem('companySelected'));
          userCopy.farm = companySelected ? companySelected.id : null;
        }
        return this.http
          .post<User>(this.apiBaseUrl + 'api/private/v2/auth/users/', userCopy)
          .pipe(map((response) => response));
      }
    }
  }
  deleteUser(id: number): Observable<User> {
    return this.http.delete(this.apiBaseUrl + 'api/private/v2/auth/users/' + id).pipe(
      map((response) => {
        return { id } as User;
      })
    );
  }

  getUserById(id: number): Observable<User> {
    return new Observable<User>((observer) => {
      this.userList$
        .pipe(
          first((list) => list && list.length > 0),
          map((list: User[]) => {
            const userSelected = list.find((user) => user.id === id);
            return userSelected ? userSelected : null;
          })
        )
        .subscribe((user) => {
          observer.next(user);
          observer.complete();
        });
    });
  }

  getUserById2 = (id: number): User => {
    let item: User;
    this.userList$
      .pipe(first((list: User[]) => list && list.length > 0))
      .subscribe((list: User[]) => {
        item = list.find((i) => i.id === id);
      });
    return item;
  };

  getIdByUsername = (username: string): Observable<User> => {
    return this.userList$.pipe(
      map((users) => {
        return users.find((user) => user.username === username);
      })
    );
  };

  saveSurvey(survey): Observable<Survey> {
    delete survey.lastUpdated;
    delete survey.dateCreated;

    const companySelected = JSON.parse(localStorage.getItem('companySelected'));
    survey.companyId = companySelected ? companySelected.id : null;

    return this.http
      .post<Survey>(this.apiBaseUrl + 'api/private/v2/surveys/', survey)
      .pipe(map((response) => response));
  }

  enableUser(user) {
    return this.http
      .put(this.apiBaseUrl + 'api/private/v2/auth/users/' + user.id, user)
      .pipe(map((response) => response));
  }

  manageRemovedUser(allUserList) {
    allUserList.forEach((i) => {
      if (i.enabled == false || i.accountLocked == true) {
        i.lastName =
          i.lastName +
          ' - (' +
          this.translate.instant('users_select.remove_on', {}) +
          ' ' +
          moment(i.lastUpdated.toString()).utc().format('DD-MM-YYYY') +
          ')';
      }
    });
    return allUserList;
  }

  sendToEquipment(equipment: Equipment) {
    const companyId = equipment.companyId ? equipment.companyId : null;

    const resource = equipment.externalResources.find(
      (r) => r.externalSource === 'FADE' || r.externalSource === 'CLAAS'
    );
    const { externalSource } = resource;
    const { externalId } = resource;

    const url = `${this.apiBaseUrl}api/private/v3/isoxml/users/${companyId}/send?externalSource=${externalSource}&equipmentId=${externalId}`;
    return this.http.post(url, {});
  }

  getUserSettings(): Observable<any[]> {
    this.commoditySetting = [];
    return this.http.get<any[]>(`${this.apiBaseUrl}api/private/v3/analytics/user-settings`).pipe(
      map((response) => this.setCommoditySettingList(response)),
      catchError((error) => {
        if (error.status === 404) {
          return this.saveUserSettingsCommodities([]).pipe(switchMap(() => this.getUserSettings()));
        }
        return of([]);
      })
    );
  }

  saveUserSettingsCommodities(commodityList: any[]): Observable<any[]> {
    this.commoditySetting = [...new Set(commodityList)];
    const obj = { lastSelectedCommodities: this.commoditySetting };
    return this.http
      .post<any[]>(`${this.apiBaseUrl}api/private/v3/analytics/user-settings`, obj)
      .pipe(map((response) => response));
  }

  saveUserSettingsSupplyChainDestinationFilterActive(isOnly: boolean): Observable<any[]> {
    this.isSupplyChainDestinationFilterActive = isOnly;
    const obj = { isSupplyChainDestinationFilterActive: this.isSupplyChainDestinationFilterActive };
    return this.http
      .post<any[]>(`${this.apiBaseUrl}api/private/v3/analytics/user-settings`, obj)
      .pipe(map((response) => response));
  }

  setCommoditySettingList(settings: any) {
    this.commoditySetting = settings.lastSelectedCommodities;
    this.isSupplyChainDestinationFilterActive = settings.isSupplyChainDestinationFilterActive;
    this.showSupplyChainDestinationFilter = settings.showSupplyChainDestinationFilter;
    return settings;
  }

  sendQdcaRequest(preferredEmail: string) {
    const url = `${this.apiBaseUrl}api/private/v1/store/qdca`;
    const params = new HttpParams().set('preferredEmail', preferredEmail);

    return this.http.post(url, {}, { params });
  }

  createLeadRequest(phone: number, email?: string) {
    const url = `${this.apiBaseUrl}api/private/v1/store/lead/qdca`;
    const params = new HttpParams({
      fromObject: {
        phone: phone.toString(),
        ...(email ? { preferredEmail: email } : {}),
      },
    });
    return this.http.post(url, {}, { params });
  }
}
